// DriveAway (Auto Europe) campervan/car hire affiliate.
//
// Handoff philosophy mirrors Bookeasy: all content lives on our pages;
// the user is only passed to DriveAway at the moment they choose to
// book, via the white-label page (Camping Australia branded).
//
// Affiliate account: CAMPAUS (8% per completed booking, paid the month
// after vehicle drop-off). Reports: driveaway.com.au affiliate portal.

export const DRIVEAWAY_AFF = 'CAMPAUS';

const WHITE_LABEL_BASE = 'https://booking.driveaway.com.au/';

export type HirePlacement =
  | 'hire-page-hero'
  | 'hire-page-vehicle-card'
  | 'hire-page-footer'
  | 'trip-planner-sidebar'
  | 'campsite-detail-sidebar'
  | 'site-footer'
  | 'webmcp';

/**
 * Optional deeplink prefill for the Auto Europe / DriveAway white-label
 * booking engine (per their WLS tutorial). Preloads the search form so
 * the user lands on results for their trip, not an empty form.
 *   - pickupCity / dropoffCity: ENGLISH city name, e.g. "SYDNEY". The
 *     builder upper-cases and %20-encodes it. Dropoff is for one-way.
 *   - pickupDate: JS Date or ISO yyyy-mm-dd; split into pu day/month/year.
 */
export type HirePrefill = {
  pickupCity?: string;
  dropoffCity?: string;
  pickupDate?: string;
};

/**
 * White-label booking URL with the affiliate code, a per-placement
 * utm_content tag, and optional trip prefill. DriveAway attributes
 * commission off `aff`; utm params are for our own reporting (they pass
 * through harmlessly). `pucode=AU` preloads Australia so the form is
 * never empty.
 */
export function driveawayBookingUrl(
  placement: HirePlacement,
  prefill?: HirePrefill,
): string {
  const u = new URL(WHITE_LABEL_BASE);
  u.searchParams.set('aff', DRIVEAWAY_AFF);
  u.searchParams.set('pucode', 'AU');
  if (prefill?.pickupCity) {
    u.searchParams.set('pucity', prefill.pickupCity.trim().toUpperCase());
  }
  if (prefill?.dropoffCity) {
    u.searchParams.set('docode', 'AU');
    u.searchParams.set('docity', prefill.dropoffCity.trim().toUpperCase());
  }
  if (prefill?.pickupDate) {
    const d = new Date(prefill.pickupDate);
    if (!Number.isNaN(d.getTime())) {
      u.searchParams.set('puday', String(d.getDate()).padStart(2, '0'));
      u.searchParams.set('pumonth', String(d.getMonth() + 1).padStart(2, '0'));
      u.searchParams.set('puyear', String(d.getFullYear()));
    }
  }
  u.searchParams.set('utm_source', 'campingaustralia');
  u.searchParams.set('utm_medium', 'referral');
  u.searchParams.set('utm_content', placement);
  // The WLS wants %20 for spaces in city names; URL encodes spaces as
  // '+' in query strings, so normalise to %20.
  return u.toString().replace(/\+/g, '%20');
}

/**
 * Fire-and-forget outbound click event. gtag isn't wired into the
 * client-app yet — this no-ops until it is, at which point every
 * existing placement starts reporting without a code change.
 */
export function trackHireClick(placement: HirePlacement): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.('event', 'hire_outbound_click', {
    event_category: 'campervan_hire',
    event_label: placement,
  });
}
